import { validatenull } from './validate'
import request from '@/router/axios'
import * as CryptoJS from 'crypto-js'
import { ElMessage, ElNotification, ElLoading } from 'element-plus'

let debounceTimer = null
let debounceLoading = false

export function debounce(func, wait = 800) {
  if (typeof func !== 'function' || debounceLoading) {
    console.warn('func must be a function')
    return
  }
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
  debounceTimer = setTimeout(() => {
    const result = func()
    if (result instanceof Promise) {
      debounceLoading = true
      result.then(() => {
        debounceLoading = false
      })
    }
  }, wait)
}

export function mergeRecursive(source, target) {
  for (const index in target) {
    try {
      if (target[index].constructor === Object) {
        source[index] = mergeRecursive(source[index], target[index])
      } else {
        source[index] = target[index]
      }
    } catch {
      source[index] = target[index]
    }
  }
  return source
}
export function selectDictLabel(datas, value) {
  if (value === undefined) {
    return ''
  }
  const actions = []
  Object.keys(datas).some(key => {
    if (datas[key].value === '' + value) {
      actions.push(datas[key].label)
      return true
    }
  })
  if (actions.length === 0) {
    actions.push(value)
  }
  return actions.join('')
}
export function serialize(data) {
  const list = []
  Object.keys(data).forEach(ele => {
    list.push(`${ele}=${data[ele]}`)
  })
  return list.join('&')
}
export function getObjType(obj) {
  var toString = Object.prototype.toString
  var map = {
    '[object Boolean]': 'boolean',
    '[object Number]': 'number',
    '[object String]': 'string',
    '[object Function]': 'function',
    '[object Array]': 'array',
    '[object Date]': 'date',
    '[object RegExp]': 'regExp',
    '[object Undefined]': 'undefined',
    '[object Null]': 'null',
    '[object Object]': 'object'
  }
  if (obj instanceof Element) {
    return 'element'
  }
  return map[toString.call(obj)]
}
export function simpleClone(data) {
  return JSON.parse(JSON.stringify(data))
}
export function deepClone(data, hash = new WeakMap()) {
  // Handle null and primitives early
  if (data === null) {
    return null
  }

  if (typeof data !== 'object') {
    return data
  }

  // Handle Date objects
  if (data instanceof Date) {
    return new Date(data.getTime())
  }

  // Handle RegExp objects
  if (data instanceof RegExp) {
    return new RegExp(data)
  }

  // Handle circular references
  if (hash.has(data)) {
    return hash.get(data)
  }
  // Handle Map
  if (data instanceof Map) {
    const mapObj = new Map()
    hash.set(data, mapObj)
    data.forEach((value, key) => {
      mapObj.set(key, deepClone(value, hash))
    })
    return mapObj
  }

  // Handle Set
  if (data instanceof Set) {
    const obj = new Set()
    hash.set(data, obj)
    data.forEach(value => {
      obj.add(deepClone(value, hash))
    })
    return obj
  }

  const type = getObjType(data)
  let obj

  // Handle Array and Object
  if (type === 'array') {
    obj = []
    hash.set(data, obj)
    for (let i = 0, len = data.length; i < len; i++) {
      obj.push(deepClone(data[i], hash))
    }
  } else if (type === 'object') {
    obj = {}
    hash.set(data, obj)
    for (let key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        obj[key] = deepClone(data[key], hash)
      }
    }
  }

  return obj
}

/**
 * 判断是否相等，包括 Object, Array, String, Number, Boolean，支持可以忽略的字段（ignoredFields）
 *
 * @param {*} obj1
 * @param {*} obj2
 * @param {*} ignoredFields
 */

export function isSame(obj1, obj2, ignoredFields = []) {
  // compare them directly if any of them is not object
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return obj1 === obj2
  }

  // Handle Date objects separately
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime()
  }

  // Handling unordered comparisons of arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    // Return false directly if the array length is different
    if (obj1.length !== obj2.length) return false

    // Create a copy to avoid modifying the original array
    const arr2Copy = [...obj2]

    // Traverse each element of the first array
    return (
      obj1.every(item1 => {
        // Search for matches in the replica array
        const matchIndex = arr2Copy.findIndex(item2 => isSame(item1, item2, ignoredFields))

        if (matchIndex > -1) {
          // Remove matched elements to avoid duplicate comparisons
          arr2Copy.splice(matchIndex, 1)
          return true
        }
        return false
      }) && arr2Copy.length === 0
    ) // Ensure that all replica arrays match completely
  }

  // Filter the key list after ignored fields
  const keys1 = Object.keys(obj1).filter(key => !ignoredFields.includes(key))
  const keys2 = Object.keys(obj2).filter(key => !ignoredFields.includes(key))

  // Check if the number of keys are the same
  if (keys1.length !== keys2.length) {
    return false
  }

  // Iterate over keys
  for (let key of keys1) {
    // Ignore functions
    if (typeof obj1[key] === 'function' && typeof obj2[key] === 'function') {
      continue
    }

    // Check if obj2 has the same key
    if (obj1[key] && !obj2[key]) {
      return false
    }

    // Recursively compare values
    if (!isSame(obj1[key], obj2[key], ignoredFields)) {
      return false
    }
  }
  return true
}
export function setTheme(name) {
  document.body.className = name
}
export function encodePassword(password) {
  const key = CryptoJS.enc.Latin1.parse('a9f995e2400bd0f2')
  const encrypted = CryptoJS.AES.encrypt(password, key, {
    iv: key,
    mode: CryptoJS.mode.CFB,
    padding: CryptoJS.pad.NoPadding
  })
  return encrypted.toString()
}
export function encryption(params) {
  const { data, type, param } = params
  const result = JSON.parse(JSON.stringify(data))
  if (type === 'Base64') {
    param.forEach(ele => {
      result[ele] = btoa(result[ele])
    })
  } else {
    param.forEach(ele => {
      result[ele] = encodePassword(result[ele])
    })
  }
  return result
}
export function findParent(menu, id) {
  for (let i = 0; i < menu.length; i++) {
    if (menu[i].children.length !== 0) {
      for (let j = 0; j < menu[i].children.length; j++) {
        if (menu[i].children[j].id === id) {
          return menu[i]
        } else {
          if (menu[i].children[j].children.length !== 0) {
            return findParent(menu[i].children[j].children, id)
          }
        }
      }
    }
  }
}
export function loadThirdPartyIcons(url) {
  const link = document.createElement('link')
  link.type = 'text/css'
  link.rel = 'stylesheet'
  link.href = url
  const head = document.getElementsByTagName('head')[0]
  head.appendChild(link)
}
export function openWindow(url, title, w, h) {
  // Fixes dual-screen position                            Most browsers       Firefox
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : screen.left
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : screen.top

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : screen.width
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : screen.height

  const left = width / 2 - w / 2 + dualScreenLeft
  const top = height / 2 - h / 2 + dualScreenTop
  const feature =
    'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=yes, copyhistory=no, width=' +
    w +
    ', height=' +
    h +
    ', top=' +
    top +
    ', left=' +
    left
  const newWindow = window.open(url, title, feature)

  // Puts focus on the newWindow
  if (window.focus) {
    newWindow.focus()
  }
}

/**
 * 下载文件
 * @param {
 *  bucketName: string, // 桶名称
 *  fileName: string, // 文件名称
 *  original: string // 原始文件名称
 * } file
 */
export function downloadFile(file) {
  downBlobFile('/admin/sys-file/' + file.bucketName + '/' + file.fileName, {}, file.original)
}

/**
 *
 * @param url 目标下载接口
 * @param query 查询参数
 * @param fileName 文件名称
 * @returns {*}
 */
export function downBlobFile(url, query, fileName) {
  const loadingInstance = ElLoading.service({
    fullscreen: true,
    lock: true,
    background: 'rgba(0, 0, 0, 0.7)',
    text: '正在导出...'
  })
  return request({
    url: url,
    method: 'get',
    responseType: 'blob',
    params: query,
    timeout: 180000
  }).then(response => {
    // 处理返回的文件流
    const blob = response
    if (blob && blob.size === 0) {
      ElNotification({
        message: '内容为空，无法下载',
        type: 'error'
      })
      return
    }
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    window.setTimeout(function () {
      URL.revokeObjectURL(blob)
      document.body.removeChild(link)
    }, 0)
    loadingInstance.close()
  }).catch(() => {
    loadingInstance.close()
  })
}

/**
 *  <img> <a> src 处理
 * @returns {PromiseLike<T | never> | Promise<T | never>}
 */
export function handleImg(url, id) {
  return validatenull(url)
    ? null
    : request({
        url: url,
        method: 'get',
        responseType: 'blob'
      }).then(response => {
        // 处理返回的文件流
        const blob = response
        const img = document.getElementById(id)
        img.src = URL.createObjectURL(blob)
        window.setTimeout(function () {
          window.URL.revokeObjectURL(blob)
        }, 0)
      })
}

export function getQueryString(url, paramName) {
  const queryStart = url.indexOf('?')
  if (queryStart === -1) {
    return null
  }
  const queryString = uri.substring(queryStart + 1)
  const params = queryString.split('&').reduce((result, param) => {
    const [key, value] = param.split('=')
    result[key] = value
    return result
  }, {})

  return params[paramName]
}

/**
 * 计算屏幕大小
 * @returns {number}
 */
export function calcScreen() {
  const width = document.body.clientWidth
  if (width >= 1200) {
    return 3 // 大屏幕
  } else if (width >= 992) {
    return 2 // 中屏幕
  } else if (width >= 768) {
    return 1 // 小屏幕
  } else {
    return 0 // 超小屏幕
  }
}

//数字补零方法
export function padLeft(num, padString = '00') {
  const strNum = num + ''
  return (padString + strNum).substring(strNum.length)
}
export function overTwoDecimalPoints(value) {
  return value && !/^-?\d+(\.\d{0,2})?$/.test(value)
}

export function overTenThousandYuan(value) {
  return !/^(?:\d{1,6}(?:\.\d{1,6})?|\.\d{1,6})$/.test(value)
}

// 格式化2位小数
export function num2(v, needFormat = false, digital) {
  if (needFormat && (v > 99999 || -v > 9999)) {
    // 转换成万
    return Math.round((100 * v) / 10000) / 100 + '万'
  }
  return toFloat(v, digital)
}

//转化为整数
export function toInt(v) {
  if (!v || isNaN(v)) {
    return 0
  } else {
    return parseInt(v)
  }
}

/**
 *转化为浮点数
 * @param v 要转化的参数
 * @param digital 保留小数点位数
 */
export function toFloat(v, digital = 2) {
  if (!v || isNaN(v)) {
    return 0
  } else {
    if (digital) {
      return Math.round(Math.pow(10, digital) * v) / Math.pow(10, digital)
    } else {
      return parseFloat(v)
    }
  }
}

export function isNum(value) {
  return value !== null && value !== '' && !isNaN(value)
}

//转化为万计数，例如100000 = 10万
export function formatAmount(v) {
  if (v > 99999) {
    return Math.round((100 * v) / 10000) / 100 + '万'
  }
  return v
}

/**
 * 数字转化为文字大写格式
 */
export function bigNumber(v) {
  if (!v) {
    return v
  }
  let num = typeof v === 'string' ? parseFloat(v) : v
  let strOutput = '',
    strUnit = '仟佰拾亿仟佰拾万仟佰拾元角分'
  num += '00'
  const intPos = num.indexOf('.')
  if (intPos >= 0) {
    num = num.substring(0, intPos) + num.substr(intPos + 1, 2)
  }
  strUnit = strUnit.substr(strUnit.length - num.length)
  for (let i = 0; i < num.length; i++) {
    strOutput += '零壹贰叁肆伍陆柒捌玖'.substr(num.substr(i, 1), 1) + strUnit.substr(i, 1)
  }
  return strOutput
    .replace(new RegExp('零角零分$'), '整')
    .replace(new RegExp('零[仟佰拾]', 'g'), '零')
    .replace(new RegExp('零{2,}', 'g'), '零')
    .replace(new RegExp('零([亿|万])', 'g'), '$1')
    .replace(new RegExp('零+元'), '元')
    .replace(new RegExp('亿零{0,3}万'), '亿')
    .replace(new RegExp('^元'), '零元')
}

/**
 *去重
 * @param array 去重数组
 * @param key 去重条件
 */
export function unique(array, key) {
  const result = []

  array.forEach(arr => {
    if (!result.some(x => x[key] === arr[key])) {
      result.push(arr)
    }
  })
  return result
}

/**
 *数字转化为百分比
 * @param value 需要转化的数
 */
export function toPercent(value) {
  return (value * 100).toFixed(2) + '%'
}

/**
 *百分比转化为数字
 * @param value 需要转化的数
 */
export function fromPercent(value) {
  return Number(value.replace('%', '')) / 100
}

export function encryptUser(params) {
  return encryption({
    data: params,
    param: ['password']
  })
}

/**
 * 表格合并单元格
 */
export function getObjectSpanArr(data, property) {
  const objectSpanArr = []
  let idx = 0
  data.forEach((item, index) => {
    if (index === 0) {
      objectSpanArr.push(1)
      idx = 0
    } else {
      if (data[index][property] === data[index - 1][property]) {
        objectSpanArr[idx] += 1
        objectSpanArr.push(0)
      } else {
        objectSpanArr.push(1)
        idx = index
      }
    }
  })
  return objectSpanArr
}

/**
 * 数组对象去重
 */
export function uniqueObjectArray(array, property) {
  const newArr = array.filter((item, index) => {
    return index === array.findIndex(obj => obj[property] === item[property])
  })
  return newArr
}

/**
 * 复制文本
 */
export async function copy(data) {
  // 直接用浏览器copy功能，如果是现代浏览器
  if (navigator?.clipboard?.writeText) {
    return await navigator.clipboard.writeText(data)
  }
  // 如果是老旧的浏览器，则模拟复制粘贴
  // 模拟 输入框
  const cInput = document.createElement('input')

  // 设置隐藏样式
  cInput.style.position = 'absolute'
  cInput.style.left = '-9999px'

  cInput.value = data
  document.body.appendChild(cInput)
  cInput.select() // 选取文本框内容

  // 执行浏览器复制命令
  // 复制命令会将当前选中的内容复制到剪切板中（这里就是创建的input标签）
  // Input要在正常的编辑状态下原生复制方法才会生效
  document.execCommand('copy')

  // 复制成功后再将构造的标签 移除
  document.body.removeChild(cInput)
}
/**
 * 是否是json数据
 */
export function isJSON(str) {
  try {
    if (str) {
      JSON.parse(str)
      return true
    }
  } catch {
    return false
  }
}
/**
 * 找出产品名称name、规格型号spec、详细配置specDetail都相同的产品
 */
export function findDuplicateRecords(list) {
  const recordMap = new Map()
  for (const obj of list) {
    const key = `${(obj.name && obj.name.trim()) || ''}-${(obj.spec && obj.spec.trim()) || ''}-${(obj.specDetail && obj.specDetail.trim()) || ''}-${(obj.storageId && obj.storageId.trim()) || ''}`
    if (recordMap.has(key)) {
      recordMap.get(key).push(obj)
    } else {
      recordMap.set(key, [obj])
    }
  }
  const duplicateRecords = []
  for (const [records] of recordMap.entries()) {
    if (records.length > 1) {
      duplicateRecords.push(...records)
    }
  }
  return duplicateRecords
}
/**
 * 相同库存产品合并成一条记录（最早入库的那条数据）
 */
export function mergeObjects(list) {
  const mergedMap = new Map()
  const newList = [
    ...list.sort((a, b) => new Date(a.lastStorageTime) - new Date(b.lastStorageTime))
  ]
  newList.forEach(x => {
    const key =
      ((x.name && x.name.trim()) || '') +
      ((x.spec && x.spec.trim()) || '') +
      ((x.specDetail && x.specDetail.trim()) || '') +
      ((x.storageId && x.storageId.trim()) || '')
    if (!mergedMap.has(key)) {
      mergedMap.set(key, {
        ...x,
        unitPriceSum: (x.amountRemain + x.quantityWaitingOut) * x.unitPrice
      })
    } else {
      const existingObj = mergedMap.get(key)
      existingObj.unitPriceSum += (x.amountRemain + x.quantityWaitingOut) * x.unitPrice
      existingObj.amountRemain += x.amountRemain
      existingObj.quantity += x.quantity
      existingObj.quantityOut += x.quantityOut
      existingObj.quantityWaitingOut += x.quantityWaitingOut
    }
  })
  const resultArray = Array.from(mergedMap.values()).map(obj => {
    obj.unitPrice = num2(obj.unitPriceSum / (obj.amountRemain + obj.quantityWaitingOut))
    delete obj.unitPriceSum
    return obj
  })
  return resultArray
}

export function getThirdAuthUrl(thirdpart, type) {
  let url
  const redirect_uri = encodeURIComponent(window.location.origin + '/#/authredirect')
  if (thirdpart === 'wechat') {
    const appid = 'wxd1678d3f83b1d83a'
    url = `https://open.weixin.qq.com/connect/qrconnect?appid=${appid}&redirect_uri=${redirect_uri}&state=WX-${type}&response_type=code&scope=snsapi_login#wechat_redirect`
  } else if (thirdpart === 'tencent') {
    const client_id = '101322838'
    url = `https://graph.qq.com/oauth2.0/authorize?response_type=code&state=QQ-${type}&client_id=${client_id}&redirect_uri=${redirect_uri}`
  } else if (thirdpart === 'gitee') {
    const client_id = '235ce26bbc59565b82c989aa3a407ce844cf59a7c5e0f9caa9bb3bf32cee5952'
    url = `https://gitee.com/oauth/authorize?response_type=code&client_id=${client_id}&state=GITEE-${type}&redirect_uri=${redirect_uri}`
  } else if (thirdpart === 'osc') {
    const client_id = 'uLJ41IGu7qAGmzSVHwF4'
    url = `https://www.oschina.net/action/oauth2/authorize?response_type=code&client_id=${client_id}'&state=OSC-${type}&redirect_uri=${redirect_uri}`
  } else if (thirdpart === 'cas') {
    const returnUrl = encodeURIComponent(
      window.location.origin + `?state=CAS-${type}/#/authredirect`
    )
    url = `http://127.0.0.1:8080/cas/login?service=${returnUrl}`
  }
  return url
}

export function removeHtmlFormats(content) {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = content
  return tempDiv.textContent || tempDiv.innerText
}

/**
 * 时间戳 + 随机数结合生成唯一ID
 */
export function generateUniqueId() {
  // 获取当前时间戳（毫秒级）
  const timestamp = Date.now()
  // 生成一个随机数，并将其转换为字符串，然后取固定长度的子字符串作为随机部分
  const randomPart = Math.random().toString(36).substr(2, 9)
  // 将时间戳和随机部分组合成唯一ID
  return `${timestamp}-${randomPart}`
}

const messageInstance = new Map()
/**
 * Shows a message using Element UI Message component
 * @param {Object} options - Message configuration options
 * @param {string} options.message - Content of the message
 * @param {string} [options.type='info'] - Type of message (success/warning/info/error)
 * @param {number} [options.duration] - Display duration in milliseconds. If type is error, duration is 0 (no auto-close). Otherwise defaults to 3000ms
 * @param {boolean} [options.showClose] - Whether to show close button. Defaults to true for error messages
 * @param {Function} [options.onClose] - Callback function when message is closed
 */
export const showMessage = options => {
  const key = options.message + options.type
  if (messageInstance.has(key)) {
    return
  }
  const instance = ElMessage({
    ...options,
    duration: options.duration || (options.type === 'error' ? 0 : 3000),
    showClose: options.type === 'error',
    onClose: () => messageInstance.delete(key)
  })
  messageInstance.set(key, instance)
}

export function getUploadedFileUrl(url) {
  if (!url || url.startsWith('http') || !url.startsWith('/admin/sys-file/')) {
    return url
  }
  return import.meta.env.VITE_API_URL + url
}

export const formatFileSize = bytes => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const isImage = fileName => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
  const extension = fileName.split('.').pop().toLowerCase()
  return imageExtensions.includes(extension)
}
